'use client';

import {login} from '@/app/(auth)/login/actions';
import ErrorMessage from '@/components/errorMessage/ErrorMessage';
import LoadingButton from '@/components/loadingButton/LoadingButton';
import PasswordInput from '@/components/passwordInput/PasswordInput';
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import useInvite from '@/hooks/use-invite';
import {zodResolver} from '@hookform/resolvers/zod';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useState, useTransition} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'sonner';
import {z} from 'zod';

const formSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, {message: 'Password must be at least 8 characters'}),
});

type FormData = z.infer<typeof formSchema>;

export default function LoginForm() {
  const [loading, startTransition] = useTransition();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const {inviteeEmail: email} = useInvite() ?? {};
  const form = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email ?? '',
      password: '',
    },
  });
  const emailIsReadonly = Boolean(email);

  function handleOnSubmit(values: FormData) {
    setErrorMessage(null);

    startTransition(async () => {
      // Get all current search params
      const currentParams = Object.fromEntries(searchParams.entries());

      // If this is a household full invite, remove all invite-related params
      if (currentParams.s === 'HOUSEHOLD_FULL') {
        // Remove all invite-related parameters
        const redirectTo = '/dashboard';

        const {error} = await login({
          ...values,
          redirectTo,
        });
        if (error) {
          setErrorMessage(error);
        }
        return;
      }

      // For non-household-full cases, keep existing behavior
      const queryString = searchParams.toString();
      const redirectTo = `/dashboard${queryString ? `?${queryString}` : ''}`;
      const {error} = await login({
        ...values,
        redirectTo,
      });
      if (error) {
        setErrorMessage(error);
      }
    });
  }

  function handleOnError() {
    toast.error('Please correct the errors in the form');
  }

  return (
    <Form {...form}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleOnSubmit, handleOnError)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="email"
          render={({field}) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input autoComplete="email" type="email" readOnly={emailIsReadonly} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({field}) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput autoComplete="current-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <ErrorMessage errorMessage={errorMessage} />
        <LoadingButton className="mr-auto mt-6" loading={loading} type="submit">
          Sign In
        </LoadingButton>
        <p className="text-sm">
          Don&apos;t have an account?{' '}
          <Link href="/join" className="link">
            Sign up
          </Link>{' '}
          and join the Club!
        </p>
        <p className="mt-0 text-sm">
          <Link href="/forgot-password" className="link">
            Forgot your password?
          </Link>
        </p>
      </form>
    </Form>
  );
}
